import React, { useState, useEffect, useCallback, useRef } from 'react';
import type {
  SpaceAvailabilityCalendarProps,
  SpaceData,
  ReservationsData,
  AvailabilityStatus
} from './types';
import { getSpaceBookings } from '../../booking_calendar/services/ApiService';
import {
  today,
  addWeeks,
  daysDifference,
  formatAPIDate,
  calculateAvailabilityStatus,
  formatDate,
  daysDifferenceFromToday
} from './utils';
import DayItem from './DayItem';

/**
 * スペースの予約可能性を表示するカレンダーコンポーネント（デザイン重視Frame構造）
 *
 * @param spaceId スペースID
 * @param isList 一覧表示モードかどうか（true: 7日表示, false: 14日表示）
 */
const SpaceAvailabilityCalendar: React.FC<SpaceAvailabilityCalendarProps> = ({
  spaceId,
  isList
}) => {
  // isListがtrueなら7日、falseなら14日表示
  const daysToShow = isList ? 7 : 14;

  // 状態管理
  const [currentDate, setCurrentDate] = useState<Date>(today());
  const [initialDate] = useState<Date>(today());
  const [spaceData, setSpaceData] = useState<(SpaceData & ReservationsData) | null>(null);
  const [availability, setAvailability] = useState<AvailabilityStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // スペース情報と予約情報を取得
  const fetchSpaceData = useCallback(async () => {
    setLoading(true);
    try {
      const todayDate = today();
      const fetchDate = currentDate < todayDate ? todayDate : currentDate;
      // spaceIdを確実に数値に変換
      const spaceIdNumber = typeof spaceId === 'string' ? parseInt(spaceId, 10) : spaceId;
      const data = await getSpaceBookings(spaceIdNumber, fetchDate, 2);
      if (!data) {
        setError('データの取得に失敗しました');
        return;
      }
      if (!data.reservations || typeof data.reservations !== 'object') {
        data.reservations = {};
      }
      for (const [date, reservations] of Object.entries(data.reservations)) {
        if (!Array.isArray(reservations)) {
          data.reservations[date] = [];
        }
      }
      const combinedData = {
        ...data,
        id: Number(spaceId),
        name: data.name || `スペース ${spaceId}`,
        reserve_days_ahead: data.reserve_days_ahead || 30
      } as (SpaceData & ReservationsData);

      setSpaceData(combinedData);
      if (combinedData) {
        calculateAvailability(combinedData, fetchDate);
      }
    } catch (err) {
      setError('予約データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [spaceId, currentDate]);

  // 前回のcurrentDateを追跡するためのref
  const prevDateRef = useRef<Date | null>(null);

  useEffect(() => {
    // 初回マウント時または日付が変わった時のみデータを取得
    const shouldFetch =
      // 初回マウント時
      prevDateRef.current === null ||
      // 日付が変わった時
      prevDateRef.current.getTime() !== currentDate.getTime();

    if (shouldFetch) {
      fetchSpaceData();
      // 現在の日付を保存
      prevDateRef.current = new Date(currentDate);
    }
  }, [fetchSpaceData, currentDate]);

  // 予約状況に基づいて利用可能時間を計算
  const calculateAvailability = (data: SpaceData & ReservationsData, date: Date) => {
    const availabilityArray: AvailabilityStatus[] = [];
    const todayDate = today();

    for (let i = 0; i < daysToShow; i++) {
      const targetDate = new Date(date);
      targetDate.setDate(date.getDate() + i);
      if (targetDate < todayDate) continue;
      const apiDateKey = formatAPIDate(targetDate);

      // 今日を基準とした日数差を計算（reserve_days_aheadの制限チェック用）
      const daysFromToday = daysDifferenceFromToday(targetDate);
      // reserve_days_aheadを超える日付は利用不可（例：reserve_days_ahead=14なら15日以上先は不可）
      const isOverLimit = daysFromToday > data.reserve_days_ahead;

      const dayReservations = data.reservations[apiDateKey] || [];
      const openHours = 24;
      let reservedHours = 0;
      for (const reservation of dayReservations) {
        reservedHours += reservation.duration / 60;
      }
      const availableHours = Math.max(0, openHours - reservedHours);
      const symbol = calculateAvailabilityStatus(availableHours, isOverLimit);
      const displayDateKey = apiDateKey.replace(/-/g, '/');
      availabilityArray.push({
        date: displayDateKey,
        hour: availableHours,
        symbol
      });
    }
    setAvailability(availabilityArray);
  };

  // 前の週へ移動
  const goToPrevWeek = () => {
    const todayDate = today();
    if (daysDifference(todayDate, currentDate) <= 0) return;
    const weeks = isList ? 1 : 2;
    const prevDate = addWeeks(currentDate, -weeks);
    if (prevDate < todayDate) {
      setCurrentDate(new Date(todayDate));
    } else {
      setCurrentDate(new Date(prevDate));
    }
  };

  // 次の週へ移動
  const goToNextWeek = () => {
    const weeks = isList ? 1 : 2;
    const nextDate = addWeeks(currentDate, weeks);
    const todayDate = today();
    if (spaceData && daysDifference(todayDate, nextDate) > spaceData.reserve_days_ahead) {
      return;
    }
    setCurrentDate(new Date(nextDate));
  };

  // スケルトンUI用の日付配列を生成
  const generateSkeletonDays = () => {
    const skeletonArray: AvailabilityStatus[] = [];
    const startDate = new Date(currentDate);
    const todayDate = today();

    // 今日より前の日付は表示しない
    if (startDate < todayDate) {
      startDate.setTime(todayDate.getTime());
    }

    for (let i = 0; i < daysToShow; i++) {
      const targetDate = new Date(startDate);
      targetDate.setDate(startDate.getDate() + i);
      const dateString = formatDate(targetDate).replace(/-/g, '/');

      // ランダムな読み込み遅延を演出（各セルが少しずつ時間差でアニメーション）
      const delayedIndex = (i + 1) % 3;

      skeletonArray.push({
        date: dateString,
        hour: 0,
        symbol: 'loading'
      });
    }

    return skeletonArray;
  };

  const todayDate = today();
  const isPrevDisabled = daysDifference(todayDate, currentDate) <= 0 || currentDate <= todayDate;
  const isNextDisabled = spaceData &&
    daysDifference(
      todayDate,
      addWeeks(currentDate, isList ? 1 : 2)
    ) > spaceData.reserve_days_ahead;

  if (error) {
    return (
      <div className="text-red-500 p-4 bg-red-100 rounded">
        エラー: {error}
      </div>
    );
  }

  // ローディング中のスケルトンUIと実データの表示
  const daysToRender = loading ? generateSkeletonDays() : availability;

  return (
    <div className="flex items-start relative border border-border-light w-full h-full bg-base-primary">
      {/* 左ナビゲーション */}
      <button
        onClick={goToPrevWeek}
        disabled={isPrevDisabled}
        className="w-[31px] justify-center gap-2.5 p-2.5 bg-base-secondary flex items-center relative self-stretch border-border-light disabled:text-gray-400"
        type="button"
        title="前の週へ"
      >
        <div className="text-gray-400 text-[length:var(--icon-6-fill-0-font-size)] leading-[var(--icon-6-fill-0-line-height)] relative font-icon-6-fill-0 font-[number:var(--icon-6-fill-0-font-weight)] tracking-[var(--icon-6-fill-0-letter-spacing)] whitespace-nowrap [font-style:var(--icon-6-fill-0-font-style)]">
          {!isPrevDisabled && '〈'}
        </div>
      </button>

      {/* カレンダー本体 */}
      <div className={`flex basis-full flex-wrap ${loading ? 'relative' : ''}`}>
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-5 z-10" role="status" aria-label="読み込み中" />
        )}
        
        {daysToRender && daysToRender.length > 0 ? (
          daysToRender.slice(0, daysToShow).map((item, index) => (
            <DayItem
              key={`day-${item.date}-${loading ? 'skeleton' : 'data'}`}
              dateString={item.date}
              status={item.symbol}
              isFirstItem={index === 0}
              showWeekSeparator={false}
              index={index}
              isList={isList}
            />
          ))
        ) : (
          <div className="col-span-7 p-4 text-center text-secondary">
            データがありません
          </div>
        )}
      </div>

      {/* 右ナビゲーション */}
      <button
        onClick={goToNextWeek}
        disabled={isNextDisabled}
        className="w-[31px] justify-center gap-2.5 p-2.5 bg-base-secondary border-l border-border-light flex items-center relative self-stretch disabled:text-gray-400"
        type="button"
        title="翌週へ"
      >
        <div className="text-gray-400 text-[length:var(--icon-6-fill-0-font-size)] leading-[var(--icon-6-fill-0-line-height)] relative font-icon-6-fill-0 font-[number:var(--icon-6-fill-0-font-weight)] tracking-[var(--icon-6-fill-0-letter-spacing)] whitespace-nowrap [font-style:var(--icon-6-fill-0-font-style)]">
          {!isNextDisabled && '〉'}
        </div>
      </button>
    </div>
  );
};

export default SpaceAvailabilityCalendar;